'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const challenges = [
  {
    tag: '[01]',
    title: 'AI Model & Pipeline Security',
    desc: 'LLM prompt injection, model extraction, training data poisoning, and RAG pipeline attacks are emerging threats that traditional security tooling was not built to detect or prevent.',
  },
  {
    tag: '[02]',
    title: 'Data Governance & Privacy at Scale',
    desc: 'AI and data platforms process vast quantities of personal and sensitive data. GDPR, the EU AI Act, and emerging AI governance frameworks impose obligations that require engineering, not just policy.',
  },
  {
    tag: '[03]',
    title: 'MLOps & Supply Chain Risk',
    desc: 'Model registries, pre-trained weights, third-party datasets, and open-source ML libraries introduce supply chain risk that is poorly understood and rarely assessed.',
  },
  {
    tag: '[04]',
    title: 'Agentic AI & Autonomous Systems',
    desc: 'Agentic AI systems that take real-world actions introduce novel attack surfaces. Tool-use, memory persistence, and multi-agent orchestration create security boundaries that do not exist in traditional software.',
  },
];

const services = [
  { label: 'AI Penetration Testing', href: '/services/ai-pentesting', desc: 'LLM prompt injection, RAG pipeline attacks, model extraction, and agentic AI exploitation. OWASP LLM Top 10 and MITRE ATLAS aligned.' },
  { label: 'Threat Modelling for AI Systems', href: '/services/threat-modelling', desc: 'Architecture-level threat modelling for AI pipelines, model serving infrastructure, and agentic systems, before vulnerabilities reach production.' },
  { label: 'AppSec for Data Platforms', href: '/services/appsec', desc: 'SAST, DAST, and SCA embedded into MLOps pipelines. Vulnerability management across data ingestion, processing, and serving layers.' },
  { label: 'Compliance for AI & Data', href: '/services/compliance', desc: 'EU AI Act readiness, GDPR data processing obligations, and SOC 2 for AI-powered products. Continuous compliance, not point-in-time assessments.' },
  { label: 'IaC & Pipeline Security', href: '/services/iac-pipeline', desc: 'Infrastructure-as-code security for cloud-native ML infrastructure, GPU workloads, and data pipelines deployed at scale.' },
  { label: 'Security Focus Programme', href: '/services/security-focus', desc: 'Auditor-aligned prioritisation for AI and data companies. Cut through the noise of emerging AI governance obligations and focus on what closes real risk.' },
];

const outcomes = [
  { metric: 'EU AI Act', label: 'High-risk AI system obligations mapped and evidenced' },
  { metric: 'OWASP LLM', label: 'Top 10 vulnerabilities assessed and remediated' },
  { metric: 'GDPR', label: 'AI data processing obligations met with engineering controls' },
  { metric: 'SOC 2', label: 'Type II certification for AI-powered products and platforms' },
];

export default function AiDataPage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '600px', height: '600px', top: '-150px', left: '-100px', opacity: 0.3 }} />
          <div className="orb orb-teal" style={{ width: '400px', height: '400px', bottom: '0', right: '5%', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest">Industries</span>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">AI & Data</span>
          </div>
          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [AI & Data]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Security built for<br />
              <span className="text-[#80edfa]">the AI-native era.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] fade-in-up" style={{ transitionDelay: '0.15s' }}>
              AI and data companies face a threat landscape that traditional security tooling was not designed for, prompt injection, model extraction, pipeline poisoning, and emerging EU AI Act obligations. TechSonar embeds senior security engineers who understand AI systems, not just perimeter security.
            </p>
          </div>
        </div>
      </section>

      {/* Outcomes strip */}
      <section className="border-t border-[#222] py-12 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {outcomes?.map((o, i) => (
              <div key={i} className="fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-heading text-2xl text-[#80edfa] font-medium mb-2">{o?.metric}</p>
                <p className="text-xs text-[#555] leading-relaxed">{o?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">The challenge</p>
            <h2 className="font-heading text-[clamp(1.8rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              What makes AI & data security hard.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#222]">
            {challenges?.map((c, i) => (
              <div key={i} className="bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{c?.tag}</p>
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{c?.title}</h3>
                <p className="text-sm text-[#666] leading-relaxed">{c?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">How we help</p>
            <h2 className="font-heading text-[clamp(1.8rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              Services tailored to AI and data companies.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#222]">
            {services?.map((s, i) => (
              <Link
                key={i}
                href={s?.href}
                className="group bg-[#0a0f14] p-8 hover:bg-[#111519] transition-colors duration-300 fade-in-up"
                style={{ transitionDelay: `${i * 0.07}s` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-heading text-lg text-[#f5f5f8] font-medium group-hover:text-[#80edfa] transition-colors pr-4">{s?.label}</h3>
                  <svg className="w-4 h-4 text-[#333] group-hover:text-[#80edfa] transition-colors flex-shrink-0 mt-1" viewBox="0 0 16 16" fill="none">
                    <path d="M3 13L13 3M13 3H6M13 3V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className="text-sm text-[#555] leading-relaxed group-hover:text-[#888] transition-colors">{s?.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="max-w-[700px] fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">[Get started]</p>
            <h2 className="font-heading text-[clamp(2rem,4vw,3.5rem)] text-[#f5f5f8] font-medium leading-tight mb-6">
              Ready to secure your AI systems before they reach production?
            </h2>
            <p className="text-[#aaa] text-lg leading-relaxed mb-8">
              Whether you are preparing for EU AI Act compliance, need AI penetration testing, or want to build security into your ML pipelines, TechSonar can get you there. Senior engineers who understand AI, embedded, doing the work.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/contact" className="btn-primary">Book a discovery call</Link>
              <Link href="/services/ai-pentesting" className="px-6 py-3 border border-[#333] text-[#aaa] text-sm uppercase tracking-wide hover:border-[#80edfa] hover:text-[#80edfa] transition-colors font-sans">
                View AI Pentesting
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
